import type { ColumnDef } from '@tanstack/react-table';
import _ from 'lodash';
import { useCallback, useMemo } from 'react';
import { literal, union } from 'superstruct';
import useLocalStorageState from 'use-local-storage-state';

import {
	atPath,
	firstValue,
	Lens,
	lensFrom,
	tryMask,
	useUrlQueryLens,
} from '@adflow/frontend/util-router';
import type {
	DataTableItemType,
	ViewColumnTemplateColumnDto,
} from '@adflow/shared/util-column-templates';
import { omitUndefined } from '@adflow/shared/util-js';
import { parsableInteger } from '@adflow/shared/util-superstruct';

import {
	useAddColumnTemplateMutation,
	useColumnTemplateQuery,
	useDefaultColumnTemplateQuery,
	useEditColumnTemplateMutation,
} from './queries';
import type {
	ChangeColumnTemplate,
	ChangeColumnWidths,
	ColumnTemplateId,
	DataTableColumnProps,
	LocalColumnTemplate,
	SelectColumnTemplate,
	StoredColumnSettings,
} from './types';
import {
	columnDefToStoredSettings,
	DEFAULT_COLUMN_META,
	isRemoteColumnTemplateId,
	localColumnTemplateId,
} from './types';
import { omitColumnDefaults } from './util/column-data';
import { enforceColumnRules } from './util/column-rules';

/**
 * Copies and sorts `xs` to match the order of `ys`.
 */
function matchOrder<T>(
	xs: readonly T[],
	ys: readonly T[],
	areItemsEqual = (x: T, y: T) => x === y
): T[] {
	const indexInYs = (x: T) => {
		const index = ys.findIndex((y) => areItemsEqual(x, y));
		return index === -1 ? Infinity : index;
	};
	return xs.toSorted((a, b) => indexInYs(a) - indexInYs(b));
}

/**
 * Column settings used to be stored as an array, but we changed it to an object
 * of arrays. This code is for backwards compatibility with existing data.
 */
const storedColumnSettingsCompatibilitySerializer = {
	parse: (value: string) => {
		const x = JSON.parse(value);
		return Array.isArray(x) ? { [localColumnTemplateId]: x } : x;
	},
	stringify: JSON.stringify,
};

function useStoredColumnSettings<T>({
	columnsBase,
	itemType,
}: {
	columnsBase: ColumnDef<T>[];
	itemType: DataTableItemType;
}): {
	/**
	 * Get the locally stored column settings for a template.
	 * - For the local template, these settings are the only settings.
	 * - For remote templates, these settings are the user's customizations for
	 *   the current device.
	 */
	getColumnsStored: (templateId: ColumnTemplateId) => StoredColumnSettings[];
	setColumnsStored: (
		templateId: ColumnTemplateId,
		columns: StoredColumnSettings[]
	) => void;
} {
	const [columnsStored, setColumnsStored] = useLocalStorageState<
		Partial<Record<ColumnTemplateId, StoredColumnSettings[]>>
	>(`columns-${itemType}`, {
		serializer: storedColumnSettingsCompatibilitySerializer,
	});

	return {
		getColumnsStored: useCallback(
			(templateId) =>
				columnsStored?.[templateId] ??
				columnsBase.map((col) => ({ id: col.id! })),
			[columnsStored, columnsBase]
		),

		setColumnsStored: useCallback(
			(templateId, columns) => {
				setColumnsStored((prev) => ({
					...prev,
					[templateId]: omitColumnDefaults(columnsBase, columns),
				}));
			},
			[columnsBase, setColumnsStored]
		),
	};
}

const selectedTemplateIdLens = (itemType: DataTableItemType) =>
	lensFrom<qs.ParsedQs>()
		.then(atPath(itemType + '-column-template'))
		.then(
			new Lens(
				(value): ColumnTemplateId | undefined =>
					tryMask(
						firstValue(value),
						union([parsableInteger(), literal(localColumnTemplateId)])
					),
				(value?: ColumnTemplateId) =>
					typeof value === 'number' ? '' + value : value
			)
		);

function useSelectedTemplateId(itemType: DataTableItemType) {
	return useUrlQueryLens(selectedTemplateIdLens(itemType));
}

interface UseColumnTemplateProps<T> {
	// TValue is both in and out of the ColumnDef so we must use any to get a
	// supertype.
	//
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	columns: ColumnDef<T, any>[];
	itemType: DataTableItemType;

	/**
	 * Minimum allowed number of sticky columns. This is used for things like
	 * `SidebarTable` that only display the sticky columns.
	 */
	minSticky?: number | null;
}

/**
 * Returns column config based on the user's
 * - Account defaults
 * - User preferences
 * - Local storage
 */
export function useColumnTemplate<T>({
	columns: columnsBase,
	itemType,
	minSticky = null,
}: UseColumnTemplateProps<T>): DataTableColumnProps<T> {
	const { mutateAsync: addColumnTemplateMutation } =
		useAddColumnTemplateMutation(itemType);
	const { mutateAsync: editColumnTemplateMutation } =
		useEditColumnTemplateMutation(itemType);

	const [selectedTemplateId, setSelectedTemplateId] =
		useSelectedTemplateId(itemType);

	const defaultColumnTemplateQuery = useDefaultColumnTemplateQuery({
		type: itemType,
	});

	const selectedColumnTemplateQuery = useColumnTemplateQuery({
		templateId: isRemoteColumnTemplateId(selectedTemplateId)
			? selectedTemplateId
			: undefined,
		enabled: isRemoteColumnTemplateId(selectedTemplateId),
	});

	const { getColumnsStored, setColumnsStored } = useStoredColumnSettings({
		columnsBase,
		itemType,
	});

	/**
	 * An column template that is only saved to `localStorage`.
	 *
	 * Not to be confused with {@link localStorageColumns} which are
	 * customizations applied on top of another template.
	 */
	// useMemo here because we are creating a new object.
	const localColumnTemplate: LocalColumnTemplate | undefined = useMemo(
		() => ({
			id: localColumnTemplateId,
			columns: getColumnsStored(localColumnTemplateId),
		}),
		[getColumnsStored]
	);

	const defaultColumnTemplate =
		defaultColumnTemplateQuery.data === null
			? // If there is no saved default template, the local template is the default template.
				localColumnTemplate
			: defaultColumnTemplateQuery.data;

	/**
	 * The selected template if one is selected, or the default template.
	 * `undefined` if there is no template or if something is still loading.
	 */
	const activeColumnTemplate =
		selectedTemplateId === undefined
			? defaultColumnTemplate
			: selectedTemplateId === localColumnTemplateId
				? localColumnTemplate
				: selectedColumnTemplateQuery.data;

	/**
	 * The user's customizations of the current template for the current device.
	 */
	const localStorageColumns = getColumnsStored(
		// When there is no active column template, we still need to let the
		// users change column widths so we use the local template.
		activeColumnTemplate?.id ?? localColumnTemplateId
	);

	/**
	 * The final column config that is used to render the table.
	 */
	const columns = useMemo(() => {
		const appliedColumns = columnsBase.map((column) =>
			mergeColumn({
				hardCodedDefaultColumn: column,
				defaultTemplateColumn: defaultColumnTemplate?.columns.find(
					(c) => c.id === column.id
				),
				activeColumnInfoColumn: activeColumnTemplate?.columns.find(
					(c) => c.id === column.id
				),
				localStorageColumn: localStorageColumns.find((c) => c.id === column.id),
			})
		);

		const sortedColumns = activeColumnTemplate
			? matchOrder(
					appliedColumns,
					activeColumnTemplate.columns,
					(a, b) => a.id === b.id
				)
			: appliedColumns;

		enforceColumnRules({
			columns: sortedColumns,
			original: columnsBase,
			minSticky,
		});

		return sortedColumns;
	}, [
		columnsBase,
		activeColumnTemplate,
		minSticky,
		defaultColumnTemplate?.columns,
		localStorageColumns,
	]);

	const columnWidths = useMemo<Record<string, number>>(() => {
		const widthState: Record<string, number> = {};
		for (const column of columns) {
			if (column.size) {
				widthState[column.id!] = column.size;
			}
		}
		return widthState;
	}, [columns]);

	const selectColumnTemplate = useCallback<SelectColumnTemplate>(
		(templateId) => {
			setSelectedTemplateId(templateId);
		},
		[setSelectedTemplateId]
	);

	const changeColumnTemplate = useCallback<ChangeColumnTemplate>(
		async (dto) => {
			let result: { id: ColumnTemplateId };

			if (!dto.saved) {
				result = { id: localColumnTemplateId };
			} else if (isRemoteColumnTemplateId(dto.id)) {
				result = await editColumnTemplateMutation({
					id: dto.id,
					body: dto,
				});
			} else {
				result = await addColumnTemplateMutation(dto);
			}

			setColumnsStored(result.id, dto.columns);
			setSelectedTemplateId(result.id);
		},
		[
			addColumnTemplateMutation,
			setColumnsStored,
			setSelectedTemplateId,
			editColumnTemplateMutation,
		]
	);

	const changeColumnWidths = useCallback<ChangeColumnWidths>(
		(widthMap) => {
			const updatedColumns = activeColumnTemplate
				? activeColumnTemplate.columns.map((c: StoredColumnSettings) => ({
						...c,
						widthConfig: widthMap[c.id],
					}))
				: columnsBase.map((c) => ({
						...columnDefToStoredSettings(c),
						widthConfig: widthMap[c.id!],
					}));

			setColumnsStored(
				activeColumnTemplate?.id ?? localColumnTemplateId,
				updatedColumns
			);
		},
		[activeColumnTemplate, columnsBase, setColumnsStored]
	);

	const resetColumnWidths = useCallback(() => {
		changeColumnWidths({});
	}, [changeColumnWidths]);

	return {
		columns,
		columnTemplateMeta: {
			columnsBase,
			columnWidths,
			changeColumnTemplate,
			changeColumnWidths,
			resetColumnWidths,
			selectColumnTemplate,
			activeColumnTemplate,
		},
	};
}

function mergeColumn<TData, TValue>({
	hardCodedDefaultColumn,
	defaultTemplateColumn,
	activeColumnInfoColumn,
	localStorageColumn,
}: {
	hardCodedDefaultColumn: ColumnDef<TData, TValue>;
	defaultTemplateColumn?: ViewColumnTemplateColumnDto | StoredColumnSettings;
	activeColumnInfoColumn?: ViewColumnTemplateColumnDto | StoredColumnSettings;
	localStorageColumn?: StoredColumnSettings;
}): ColumnDef<TData, TValue> {
	return {
		...hardCodedDefaultColumn,

		size:
			// We want users to always be able to set their own column widths.
			localStorageColumn?.widthConfig ??
			activeColumnInfoColumn?.widthConfig ??
			defaultTemplateColumn?.widthConfig ??
			hardCodedDefaultColumn.size,

		meta: {
			...DEFAULT_COLUMN_META,
			...omitUndefined(hardCodedDefaultColumn.meta),
			..._.omit(
				{
					...omitUndefined(defaultTemplateColumn),
					...omitUndefined(activeColumnInfoColumn),
				},
				['id', 'widthConfig']
			),
		},
	};
}
